import { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Typography, Button, Chip, Alert, Snackbar,
  CircularProgress, Link,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { apiGet, apiPut } from "../../../utils/api";
import { getKeycloakConfig } from "../../../config/keycloak";

/**
 * Security → Two-factor authentication.
 *
 * Lets the signed-in user choose their second factor: an email one-time code
 * or an authenticator app (TOTP). The choice is saved to the backend, which is
 * the source of truth and mirrors it to the Keycloak `mfa_method` user
 * attribute; the Keycloak browser login flow reads that attribute to decide
 * which second factor to prompt. Password plus a second factor is always
 * enforced; this only selects which second factor, never disables MFA.
 */
const METHODS = [
  {
    id: "email",
    label: "Email code",
    Icon: MailOutlineIcon,
    blurb: "A one-time code is emailed to you at each sign-in. Nothing to install.",
  },
  {
    id: "totp",
    label: "Authenticator app",
    Icon: SmartphoneIcon,
    blurb: "Codes from an app like Google Authenticator or Authy. Works offline; stronger against phishing.",
  },
];

export default function SecurityMfaCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(null);       // persisted method
  const [selected, setSelected] = useState(null);      // pending selection
  const [totpConfigured, setTotpConfigured] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const kc = (() => { try { return getKeycloakConfig(); } catch { return null; } })();
  const setupUrl = kc?.url && kc?.realm
    ? `${kc.url.replace(/\/$/, "")}/realms/${kc.realm}/account/#/security/signing-in`
    : null;

  const load = async () => {
    try {
      setError("");
      const data = await apiGet("/user/mfa-method");
      setCurrent(data.method);
      setSelected(data.method);
      setTotpConfigured(!!data.totp_configured);
      setNeedsSetup(!!data.requires_totp_setup);
    } catch (e) {
      setError("Could not load your authentication settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!selected || selected === current) return;
    setSaving(true);
    setError("");
    try {
      const res = await apiPut("/user/mfa-method", { method: selected });
      setCurrent(res.method);
      setTotpConfigured(!!res.totp_configured);
      setNeedsSetup(!!res.requires_totp_setup);
      setToast(
        res.requires_totp_setup
          ? "Saved. You'll be guided to set up your authenticator app at your next sign-in."
          : "Two-factor method updated."
      );
    } catch (e) {
      setError(e?.message || "Could not update your authentication method. Please try again.");
      setSelected(current); // revert the pending choice on failure
    } finally {
      setSaving(false);
    }
  };

  const dirty = selected && selected !== current;

  return (
    <Card sx={{ bgcolor: "var(--fx-paper)", borderRadius: 2.5, border: "1px solid rgba(0,0,0,0.08)", width: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <ShieldOutlinedIcon sx={{ fontSize: 20, color: "#2563eb" }} />
          <Typography variant="h6" sx={{ color: "var(--fx-text-strong)", fontWeight: 700, fontSize: "1rem" }}>
            Two-factor authentication
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "var(--fx-text-muted)", fontSize: "0.82rem", mb: 2.5 }}>
          Every sign-in requires your password <strong>and</strong> a second factor. Choose the second factor
          you'd like to use.
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {METHODS.map(({ id, label, Icon, blurb }) => {
                const active = selected === id;
                const isCurrent = current === id;
                return (
                  <Box
                    key={id}
                    role="radio"
                    aria-checked={active}
                    tabIndex={0}
                    onClick={() => !saving && setSelected(id)}
                    onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !saving) { e.preventDefault(); setSelected(id); } }}
                    sx={{
                      flex: "1 1 220px", minWidth: 220, cursor: saving ? "default" : "pointer",
                      p: 2, borderRadius: 2, position: "relative",
                      border: active ? "2px solid #2563eb" : "1.5px solid rgba(0,0,0,0.12)",
                      bgcolor: active ? "rgba(37,99,235,0.06)" : "var(--fx-subtle)",
                      transition: "all 0.15s ease",
                      "&:hover": { borderColor: active ? "#2563eb" : "rgba(37,99,235,0.4)" },
                      outline: "none",
                      "&:focus-visible": { boxShadow: "0 0 0 3px rgba(37,99,235,0.35)" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                      <Icon sx={{ fontSize: 20, color: active ? "#2563eb" : "var(--fx-text-muted)" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--fx-text-strong)" }}>
                        {label}
                      </Typography>
                      {isCurrent && (
                        <Chip
                          size="small" label="Current"
                          sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700, bgcolor: "var(--fx-success-soft)", color: "var(--fx-success-text)" }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ color: "var(--fx-text-muted)", lineHeight: 1.5, display: "block" }}>
                      {blurb}
                    </Typography>
                    {id === "totp" && totpConfigured && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#16a34a" }} />
                        <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 600 }}>
                          Authenticator app enrolled
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {selected === "totp" && !totpConfigured && (
              <Alert severity="info" sx={{ mt: 2, fontSize: "0.8rem" }}>
                You haven't enrolled an authenticator app yet. After saving, you'll be guided through setup at
                your next sign-in{setupUrl ? <>, or set it up now in your <Link href={setupUrl} target="_blank" rel="noopener">account security page</Link>.</> : "."}
              </Alert>
            )}

            {needsSetup && current === "totp" && !totpConfigured && selected === current && (
              <Alert severity="warning" sx={{ mt: 2, fontSize: "0.8rem" }}>
                Your authenticator app isn't set up yet. You'll be prompted to enroll at your next sign-in.
              </Alert>
            )}

            {error && <Alert severity="error" sx={{ mt: 2, fontSize: "0.8rem" }}>{error}</Alert>}

            <Box sx={{ display: "flex", justifyContent: (setupUrl && (selected === "totp" || current === "totp")) ? "space-between" : "flex-end", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 2.5 }}>
              {setupUrl && (selected === "totp" || current === "totp") && (
                <Button
                  variant="outlined"
                  component="a"
                  href={setupUrl}
                  target="_blank"
                  rel="noopener"
                  startIcon={<SmartphoneIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: "none", fontWeight: 600, borderRadius: 2,
                    borderColor: "#2563eb", color: "#2563eb",
                    "&:hover": { borderColor: "#1d4ed8", backgroundColor: "rgba(37,99,235,0.06)" },
                  }}
                >
                  {totpConfigured ? "Manage authenticator app" : "Set up authenticator app"}
                </Button>
              )}
              <Button
                variant="contained"
                onClick={save}
                disabled={!dirty || saving}
                sx={{
                  backgroundColor: "#2563eb", color: "#fff", fontWeight: 600, px: 3.5, py: 1,
                  borderRadius: 2, textTransform: "none",
                  "&:hover": { backgroundColor: "#1d4ed8" },
                }}
              >
                {saving ? "Saving…" : dirty ? "Save method" : "Saved"}
              </Button>
            </Box>
          </>
        )}
      </CardContent>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setToast("")} severity="success" sx={{ width: "100%" }}>
          {toast}
        </Alert>
      </Snackbar>
    </Card>
  );
}
